import { supabase } from './lib/supabase'

export function initChat(user) {
  const toggleBtn = document.getElementById('chat-toggle-btn');
  const chatWindow = document.getElementById('chat-window');
  
  if (!toggleBtn || !chatWindow) return;
  if (window.__CHAT_INITIALIZED__) return;
  window.__CHAT_INITIALIZED__ = true;

  const closeBtn = document.getElementById('close-chat');
  const chatForm = document.getElementById('chat-form');
  const chatInput = document.getElementById('chat-input');
  const messagesArea = document.getElementById('chat-messages');
  const badge = document.getElementById('chat-unread-badge');

  // Guest Logic
  let guestId = null;
  if (!user) {
    guestId = localStorage.getItem('helix_guest_id');
    if (!guestId) {
      guestId = String('guest_' + Math.floor(Math.random() * 1000000));
      localStorage.setItem('helix_guest_id', guestId);
    }
  }

  const scrollToBottom = () => {
    if (messagesArea) messagesArea.scrollTop = messagesArea.scrollHeight;
  };

  // Open/Close Chat Window
  const openChat = () => {
    chatWindow.classList.remove('hidden');
    setTimeout(() => {
      chatWindow.classList.remove('opacity-0', 'scale-90');
      chatWindow.classList.add('opacity-100', 'scale-100');
      scrollToBottom();
      markAllAsRead();
    }, 10);
  };

  const closeChat = () => {
    chatWindow.classList.remove('opacity-100', 'scale-100');
    chatWindow.classList.add('opacity-0', 'scale-90');
    setTimeout(() => chatWindow.classList.add('hidden'), 300);
  };

  toggleBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    const isHidden = chatWindow.classList.contains('hidden');
    if (isHidden) openChat();
    else closeChat();
  });

  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    closeChat();
  });

  // Prevent clicks inside the chat window from bubbling to the toggle
  chatWindow.addEventListener('click', (e) => {
    e.stopPropagation();
  });

  // Send Message
  chatForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const content = chatInput.value.trim();
    if (!content) return;

    chatInput.value = '';
    
    const payload = {
      content: content,
      sender_role: 'user',
      ...(user ? { user_id: user.id } : { guest_id: guestId }),
      created_at: new Date().toISOString()
    };

    // Optimistic UI: show message immediately
    appendMessage(payload);
    scrollToBottom();
    
    const { data, error } = await supabase.from('support_messages').insert([payload]).select();
    if (error) {
        console.error('Chat System Check: Database Insert FAILED:', error.message);
    } else {
        console.log('Chat System Check: Database Insert SUCCESS:', data);
    }
  });

  // Load Initial Messages
  const loadMessages = async () => {
    let query = supabase.from('support_messages').select('*').eq('status', 'open');
    if (user) query = query.eq('user_id', user.id);
    else query = query.eq('guest_id', guestId);
    
    const { data, error } = await query.order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error.message);
      return;
    }

    if (data) {
      messagesArea.innerHTML = '';
      if (data.length === 0) {
          addWelcomeMessage();
      } else {
          data.forEach(msg => appendMessage(msg));
          scrollToBottom();
          updateUnreadBadge(data);
      }
    }
  };

  const appendMessage = (msg) => {
    if (!messagesArea) return;
    
    const isUser = msg.sender_role === 'user';
    const msgDiv = document.createElement('div');
    msgDiv.className = `flex ${isUser ? 'justify-end' : 'justify-start'} gap-3 items-start mb-4`;
    
    const msgTime = msg.created_at ? new Date(msg.created_at) : new Date();

    msgDiv.innerHTML = `
      ${!isUser ? '<div class="w-6 h-6 bg-zinc-800 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-black text-emerald-500 border border-zinc-700">HY</div>' : ''}
      <div class="${isUser ? 'bg-emerald-600 border-emerald-500' : 'bg-zinc-900 border-zinc-800'} border p-3 rounded-sm ${isUser ? 'rounded-tr-none' : 'rounded-tl-none'} max-w-[80%] shadow-sm">
        <p class="text-[11px] ${isUser ? 'text-white' : 'text-zinc-300'} leading-relaxed font-medium">${msg.content}</p>
        <div class="flex items-center gap-2 mt-2 ${isUser ? 'justify-end' : 'justify-start'}">
          <span class="text-[8px] ${isUser ? 'text-emerald-200' : 'text-zinc-600'} font-bold uppercase tracking-widest">
            ${isUser ? 'You' : 'Customer Support'} • ${msgTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      </div>
    `;
    
    messagesArea.appendChild(msgDiv);
    scrollToBottom();
  };

  const addWelcomeMessage = () => {
      messagesArea.innerHTML = `
        <div class="flex items-start gap-3 mb-4">
          <div class="w-6 h-6 bg-zinc-800 rounded-full flex-shrink-0 flex items-center justify-center text-[8px] font-black text-emerald-500 border border-zinc-700">HY</div>
          <div class="bg-zinc-900 border border-zinc-800 p-3 rounded-sm rounded-tl-none max-w-[80%]">
            <p class="text-[11px] text-zinc-300 leading-relaxed font-medium">Hello! Welcome to HelixYield Support. How can we assist you today?</p>
            <span class="text-[8px] text-zinc-600 font-bold uppercase tracking-widest mt-2 block text-right">Customer Support</span>
          </div>
        </div>
      `;
  };

  const updateUnreadBadge = (messages) => {
    const unread = messages.filter(m => !m.is_read && m.sender_role === 'admin').length;
    if (unread > 0 && chatWindow.classList.contains('hidden')) {
      badge.textContent = unread;
      badge.classList.remove('hidden');
    } else {
      badge.classList.add('hidden');
    }
  };

  const markAllAsRead = async () => {
    let query = supabase.from('support_messages').update({ is_read: true }).eq('sender_role', 'admin').eq('is_read', false);
    if (user) query = query.eq('user_id', user.id);
    else query = query.eq('guest_id', guestId);
    await query;
    badge.classList.add('hidden');
  };

  // Subscribe to Realtime
  const subFilter = user ? `user_id=eq.${user.id}` : `guest_id=eq.${guestId}`;
  const subscription = supabase
    .channel(`chat:${user ? user.id : guestId}`)
    .on('postgres_changes', { 
      event: '*', // Listen for ALL changes (INSERT, UPDATE)
      schema: 'public', 
      table: 'support_messages',
      filter: subFilter 
    }, (payload) => {
      // If a message was resolved, reload everything
      if (payload.new.status === 'resolved') {
          loadMessages();
          return;
      }

      // Only handle new messages from admin (User messages are handled optimistically)
      if (payload.eventType === 'INSERT' && payload.new.sender_role === 'admin') {
          appendMessage(payload.new);
          scrollToBottom();
          if (chatWindow.classList.contains('hidden')) {
              loadMessages();
          } else {
              markAllAsRead();
          }
      }
    })
    .subscribe();

  loadMessages();
}
